import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    SimpleForm,
    Edit,
    TextInput,
    BooleanInput,
    SaveButton,
    DeleteWithConfirmButton,
    Toolbar,
    Notification,
    useNotify,
    useRefresh,
    NumberInput,
    FormDataConsumer
} from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteIcon from "@material-ui/icons/Delete";
import { ThemeProvider } from "@material-ui/styles";
import { useForm } from "react-final-form";
import axios from "axios";
import customTheme from "../../customTheme";
import { apiUrl } from "../../dataProvider";
import { validateMagicNUmber, validateRequired, valueBetween } from "../../utils/Validations";
import WithProps from "../Custom/WithProps";
import CustomAdminSelect from "./CustomAdminSelect";
import VideoPreview from "./VideoPreview";

export const normalizeActiveValue = (value, fallback = false) => {
    if (value === undefined || value === null || value === "") {
        return fallback;
    }
    if (value === true || value === 1 || value === "1") {
        return true;
    }
    if (value === false || value === 0 || value === "0") {
        return false;
    }
    return Boolean(value);
};

export const isEmptyValue = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

const useStyles = makeStyles(() => ({
    root: {
        width: "90%",
        margin: "auto",
    },
    toolbar: {
        justifyContent: "space-between",
    },
    grouping: {
        border: "1px solid lightgray",
        borderRadius: "3px",
        margin: "1% auto",
    },
    warnLabel: {
        fontSize: "10px",
        color: "red",
        display: "block",
    },
    displayFlex: {
        display: "flex",
    },
    equalWidth: {
        maxWidth: "33%",
    },
    heading: {
        fontWeight: "bold",
        marginTop: "10px",
        borderBottom: "3px solid #a6a5a5ff",
        paddingBottom: "5px",
    },
    builderDeleteCell: {
        display: "flex",
        alignItems: "center",
        paddingTop: 8,
    },
    builderInfoCard: {
        padding: "12px 16px",
        marginBottom: 8,
        backgroundColor: "#f5f7fa",
        border: "1px solid #e0e4e8",
        borderLeft: "4px solid #1976d2",
        borderRadius: 4,
    },
    builderInfoTitle: {
        fontWeight: 600,
        marginBottom: 8,
        color: "#1a1a1a",
    },
    builderInfoList: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px 24px",
        margin: "0 0 0 12px",
        paddingLeft: 0,
        "& li": {
            margin: 0,
            maxWidth: "100%",
            wordBreak: "break-word",
        },
    },
}));

const EditToolbar = (props) => {
    const classes = useStyles();
    return (
        <Toolbar {...props} className={classes.toolbar}>
            <SaveButton disabled={props.pristine} />
            <DeleteWithConfirmButton />
        </Toolbar>
    );
};

EditToolbar.propTypes = {
    pristine: PropTypes.bool,
};

const BuilderPlatformDataSync = ({ bestsellerId, builderPlatformId }) => {
    const form = useForm();
    const notify = useNotify();
    const previousBuilderPlatformIdRef = useRef(builderPlatformId);

    useEffect(() => {
        const previousBuilderPlatformId = previousBuilderPlatformIdRef.current;
        previousBuilderPlatformIdRef.current = builderPlatformId;

        if (builderPlatformId === previousBuilderPlatformId) {
            return;
        }

        if (!builderPlatformId) {
            form.change("builder_platform_data", {});
            return;
        }

        const fetchBuilderPlatformData = async () => {
            try {
                const response = await axios.get(
                    `${apiUrl}/bestsellers/${bestsellerId}?builder_platform_id=${builderPlatformId}`
                );

                const builderPlatformData = response?.data?.data?.builder_platform_data;
                if (builderPlatformData) {
                    form.change("builder_platform_data", builderPlatformData);
                } else {
                    form.change("builder_platform_data", {
                        builder_platform_id: builderPlatformId,
                        active: 0,
                        discount_percentage: null,
                        similar_rings: [],
                    });
                }
            } catch (error) {
                notify(
                    error?.response?.data?.message ||
                        "Failed to fetch builder platform data",
                    { type: "warning" }
                );
            }
        };

        fetchBuilderPlatformData();
    }, [builderPlatformId, bestsellerId, form, notify]);

    return null;
};

BuilderPlatformDataSync.propTypes = {
    bestsellerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    builderPlatformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const BuilderPlatformBestsellerDeleteButton = ({ joinId }) => {
    const form = useForm();
    const notify = useNotify();
    const refresh = useRefresh();
    const classes = useStyles();
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        if (isEmptyValue(joinId) || loading) {
            return;
        }
        setLoading(true);
        try {
            await axios.delete(`${apiUrl}/builder_platform_bestseller/${joinId}`);
            notify("Builder platform removed", { type: "success" });
            form.change("builder_platform_data", {});
            refresh();
        } catch (error) {
            notify(
                error?.response?.data?.message || "Failed to remove builder platform",
                { type: "warning" }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={classes.builderDeleteCell}>
            <IconButton
                aria-label="Remove builder platform"
                onClick={handleClick}
                disabled={loading}
                size="small"
            >
                {loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            </IconButton>
        </div>
    );
};

BuilderPlatformBestsellerDeleteButton.propTypes = {
    joinId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const EditBestSeller = (props) => {
    const classes = useStyles();

    const notify = useNotify();
    const onFailure = (error) => {
        notify(
            error.response.data.message
                ? error.response.data.message
                : "Something went wrong",
            { type: "warning" }
        );
    };

    return (
        <ThemeProvider theme={customTheme}>
            <Edit mutationMode="pessimistic" onFailure={onFailure} {...props}>
                <WithProps>
                    {({ record, ...props }) => (
                        <SimpleForm
                            record={record}
                            {...props}
                            redirect="list"
                            toolbar={<EditToolbar />}
                            sanitizeEmptyValues={false}
                        >
                            <Grid
                                container
                                spacing={2}
                                className={classes.root}
                                justify="space-between"
                            >
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    className={classes.displayFlex}
                                    justify="space-between"
                                    alignItems="center"
                                >
                                    <Grid item xs={4}>
                                        <TextInput
                                            source="magic_number"
                                            label="Magic Number"
                                            validate={validateRequired}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <NumberInput
                                            source="discount_percentage"
                                            label="Discount Percentage"
                                            validate={[
                                                validateRequired,
                                                valueBetween(0, 100),
                                            ]}
                                            fullWidth
                                        />
                                    </Grid>
                                    <FormDataConsumer>
                                        {({ formData }) => {
                                            const activeValue = formData?.active;
                                            const isActive =
                                                activeValue === undefined
                                                    ? record?.active ?? true
                                                    : Boolean(activeValue);

                                            return (
                                                <Grid item xs={2}>
                                                    <BooleanInput
                                                        label={isActive ? "Active" : "Inactive"}
                                                        source="active"
                                                        initialValue={true}
                                                    />
                                                </Grid>
                                            );
                                        }}
                                    </FormDataConsumer>
                                    <Grid item xs={4}>
                                        <CustomAdminSelect
                                            label="Video URL"
                                            source="video_url"
                                            resource="getBestsellerVideoList"
                                            validate={validateRequired}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <BooleanInput
                                            label="Hide Quarter Sizes"
                                            source="hide_quarter_sizes"
                                            initialValue={false}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <BooleanInput
                                            label="Hide Outside Features In Highlights"
                                            source="hide_outside_features_in_highlights"
                                            initialValue={false}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <BooleanInput
                                            label="Hide Inside Features In Highlights"
                                            source="hide_inside_features_in_highlights"
                                            initialValue={false}
                                        />
                                    </Grid>
                                </Grid>
                                <VideoPreview />
                                <Grid item xs={5}>
                                    {[1, 2, 3, 4, 5].map((ringPosition) => (
                                        <TextInput
                                            key={`similar-ring-${ringPosition}`}
                                            source={`similar_rings[${ringPosition - 1}]`}
                                            label={`Similar Ring ${ringPosition}`}
                                            validate={validateMagicNUmber}
                                            fullWidth
                                        />
                                    ))}
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                spacing={2}
                                className={classes.root}
                                alignItems="center"
                                justify="space-between"
                            >
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    className={classes.heading}
                                >
                                    Builder Platform Configuration
                                </Grid>
                                {Array.isArray(record?.builder_platforms) &&
                                record.builder_platforms.length > 0 ? (
                                    <Grid item xs={12}>
                                        <Paper className={classes.builderInfoCard} elevation={0}>
                                            <Typography
                                                variant="subtitle2"
                                                component="h3"
                                                className={classes.builderInfoTitle}
                                            >
                                                Builders with custom configuration
                                            </Typography>
                                            <Typography variant="body2" component="div" color="textSecondary">
                                                <ul className={classes.builderInfoList}>
                                                    {record.builder_platforms.map((label, index) => (
                                                        <li key={`${label}-${index}`}>{label}</li>
                                                    ))}
                                                </ul>
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ) : null}
                                <Grid item xs={3}>
                                    <CustomAdminSelect
                                        label="Builder Platform"
                                        source="builder_platform_data.builder_platform_id"
                                        resource="builder-platform?page[number]=1&page[size]=1000"
                                        optionLabel="store_name"
                                    />
                                </Grid>
                                <FormDataConsumer>
                                    {({ formData }) => {
                                        const { builder_platform_id, id, active } = formData?.builder_platform_data || {};
                                        const isBuilderActive = normalizeActiveValue(active, false);
                                        const showBuilderFields = !isEmptyValue(builder_platform_id);
                                        const hasBuilderJoinId = isEmptyValue(id) === false;

                                        return (
                                            <>
                                                <BuilderPlatformDataSync bestsellerId={record?.id} builderPlatformId={builder_platform_id} />
                                                {showBuilderFields ? (
                                                    <>
                                                        <Grid item xs={2}>
                                                            <BooleanInput
                                                                label={isBuilderActive ? "Active" : "Inactive"}
                                                                source="builder_platform_data.active"
                                                                format={(value) =>
                                                                    normalizeActiveValue(value, false)
                                                                }
                                                                parse={(value) => (value ? 1 : 0)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <NumberInput
                                                                source="builder_platform_data.discount_percentage"
                                                                label="Builder Discount Percentage"
                                                                validate={
                                                                    isBuilderActive
                                                                        ? [validateRequired, valueBetween(0, 100)]
                                                                        : [valueBetween(0, 100)]
                                                                }
                                                                fullWidth
                                                            />
                                                        </Grid>
                                                        {hasBuilderJoinId ? (
                                                            <Grid item xs={1}>
                                                                <BuilderPlatformBestsellerDeleteButton
                                                                    joinId={id}
                                                                />
                                                            </Grid>
                                                        ) : null}
                                                        <Grid item xs={4}>
                                                            {[1, 2, 3, 4, 5].map((ringPosition) => (
                                                                <TextInput
                                                                    key={`builder-ring-${ringPosition}`}
                                                                    source={`builder_platform_data.similar_rings[${ringPosition - 1}]`}
                                                                    label={`Similar Ring ${ringPosition}`}
                                                                    fullWidth
                                                                    validate={validateMagicNUmber}
                                                                />
                                                            ))}
                                                        </Grid>
                                                    </>
                                                ) : null}
                                            </>
                                        );
                                    }}
                                </FormDataConsumer>
                            </Grid>
                        </SimpleForm>
                    )}
                </WithProps>
            </Edit>
            <Notification />
        </ThemeProvider>
    );
};

export default EditBestSeller;
